import { useState } from 'react'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import type { ClientsAdminApi } from '../../clients/index.js'
import type { FacilitiesAdminApi } from '../../facilities/index.js'
import type { RosterAdminApi } from '../../roster/index.js'
import { getFacilityBehaviors, type PartnerSession } from '../lib/partnerApi.js'
import type { EffectiveBehavior } from '../../behaviors/index.js'
import { ADMIN_API_BASE_URL, segmentedTabClass, TabStrip, EndpointHint, Skeleton } from '../../core/index.js'
import { CustomBehaviorForm } from './CustomBehaviorForm.js'
import { PartnerConnection } from './PartnerConnection.js'
import { ShiftOutcomeForm } from './ShiftOutcomeForm.js'
import { RankingForm } from './RankingForm.js'
import { StandingView } from './StandingView.js'
import { PerformanceSummaryForm } from './PerformanceSummaryForm.js'
import { BridgeEnrollmentForm } from './BridgeEnrollmentForm.js'
import { BridgeCalloutsView } from './BridgeCalloutsView.js'
import { ShiftPreferencesView } from './ShiftPreferencesView.js'
import { ScheduleRunsView } from './ScheduleRunsView.js'
import { ScheduleBoardView } from './ScheduleBoardView.js'
import { WorkerOnboardForm } from './WorkerOnboardForm.js'
import { CensusForm } from './CensusForm.js'
import { INPUT_CLASS, Labeled, Section } from './formControls.js'
import { LoadFailure } from '../../core/index.js'

/** The admin calls this screen uses. */
type AdminApi = ClientsAdminApi & FacilitiesAdminApi & RosterAdminApi

type FixedCallKind = 'shift-complete' | 'callout' | 'no-show' | 'worker-onboard' | 'ranking' | 'standing' | 'performance' | 'bridge' | 'bridge-callouts' | 'preferences' | 'schedule-run' | 'schedule-board' | 'census'
/** A fixed call, or the event of one of the client's custom behaviors (by its key). */
type CallKind = FixedCallKind | `custom:${string}`

interface CallTab {
  kind: CallKind
  label: string
  /** The scope the partner's token needs for it; null when any token for the client will do. */
  scope: string | null
  /** Set for a tab that reports a behavior this facility has switched off. */
  isOff?: boolean
}

const EVENT_TABS: (CallTab & { eventPath: string })[] = [
  { kind: 'shift-complete', label: 'Shift complete', scope: 'events:write', eventPath: '/events/shift-complete' },
  { kind: 'callout', label: 'Call-out', scope: 'events:write', eventPath: '/events/callout' },
  { kind: 'no-show', label: 'No-show', scope: 'events:write', eventPath: '/events/no-show' },
]

const OTHER_TABS: CallTab[] = [
  { kind: 'worker-onboard', label: 'Worker onboard', scope: 'events:write' },
  { kind: 'ranking', label: 'Rank a pool', scope: 'rankings:read' },
  { kind: 'standing', label: 'Worker standing', scope: null },
  { kind: 'performance', label: 'Performance summary', scope: 'performance:read' },
  { kind: 'bridge', label: 'Clinical Bridge', scope: 'events:write' },
  { kind: 'bridge-callouts', label: 'Bridge callouts', scope: 'events:write' },
  { kind: 'preferences', label: 'Shift preferences', scope: 'events:write' },
  { kind: 'schedule-run', label: 'Schedule run', scope: 'events:write' },
  { kind: 'schedule-board', label: 'Schedule board', scope: 'events:write' },
  { kind: 'census', label: 'Census', scope: 'census:write' },
]

/**
 * The calls on offer for a facility, built from what it measures: the three
 * shift events (marked off when every behavior they feed is off there), one
 * tab per custom behavior the client has defined, then the rest.
 */
function callTabsFor(behaviors: readonly EffectiveBehavior[]): CallTab[] {
  const standardTabs = EVENT_TABS.map(({ eventPath, ...tab }) => {
    const fed = behaviors.filter((behavior) => behavior.eventPath === eventPath && behavior.kind === 'standard')
    return { ...tab, isOff: fed.length > 0 && fed.every((behavior) => !behavior.enabled) }
  })
  const customTabs = behaviors
    .filter((behavior) => behavior.kind === 'custom')
    .map((behavior): CallTab => ({ kind: `custom:${behavior.key}`, label: behavior.label, scope: 'events:write', isOff: !behavior.enabled }))
  return [...standardTabs, ...customTabs, ...OTHER_TABS]
}

/** What the chosen facility measures, as the partner reads it from the API. */
function MeasuredBehaviors({ behaviors }: { behaviors: readonly EffectiveBehavior[] }) {
  return (
    <div className="border border-marine/15 bg-canvas p-3 mb-5">
      <div className="text-[10px] font-bold uppercase tracking-wide text-ink-mid mb-2">
        This facility measures <EndpointHint>GET /behaviors/facilities/…</EndpointHint>
      </div>
      <div className="flex flex-wrap gap-1.5">
        {behaviors.map((behavior) => (
          <span
            key={behavior.key}
            title={behavior.description}
            className={`inline-flex items-center gap-1.5 px-2 py-1 text-[11px] border rounded ${
              behavior.enabled ? 'bg-white border-marine/25 text-ink' : 'bg-white border-ink-mid/20 text-ink-mid line-through decoration-ink-mid/50'
            }`}
          >
            <span className="font-bold">{behavior.label}</span>
            <span className="no-underline">{behavior.enabled ? `${behavior.weight}%` : 'Off'}</span>
          </span>
        ))}
      </div>
    </div>
  )
}

function EventSender({ api, session }: { api: AdminApi; session: PartnerSession }) {
  const queryClient = useQueryClient()
  const [facilityId, setFacilityId] = useState('')
  const [kind, setKind] = useState<CallKind>('shift-complete')

  const facilitiesQuery = useQuery({
    queryKey: ['admin', 'facilities', session.clientId],
    queryFn: () => api.listFacilities(session.clientId),
  })
  const workersKey = ['admin', 'facility-workers', session.clientId, facilityId]
  const workersQuery = useQuery({
    queryKey: workersKey,
    queryFn: () => api.listFacilityWorkers(session.clientId, facilityId),
    enabled: facilityId !== '',
  })
  const workers = [...(workersQuery.data ?? [])].sort((a, b) => a.name.localeCompare(b.name) || a.workerId.localeCompare(b.workerId))

  // Read with the partner's own token: this is how a partner learns what a facility is using.
  const behaviorsQuery = useQuery({
    queryKey: ['partner-simulator', 'behaviors', session.clientId, facilityId],
    queryFn: () => getFacilityBehaviors(ADMIN_API_BASE_URL ?? '', session, facilityId),
    enabled: facilityId !== '' && Boolean(ADMIN_API_BASE_URL),
    staleTime: 0,
  })
  const behaviors = behaviorsQuery.data ?? []
  const tabs = callTabsFor(behaviors)
  // A custom behavior removed since its tab was chosen has no tab any more; fall back to the first one.
  const activeTab = tabs.find((tab) => tab.kind === kind) ?? tabs[0]
  const activeKind = activeTab?.kind ?? 'shift-complete'
  const customBehavior = activeKind.startsWith('custom:') ? behaviors.find((behavior) => `custom:${behavior.key}` === activeKind) : undefined

  const requiredScope = activeTab?.scope ?? null
  const hasScope = requiredScope === null || session.scopes.includes(requiredScope)

  return (
    <div>
      <div className="grid gap-5 sm:grid-cols-2 mb-5">
        <Labeled
          label="Facility"
          htmlFor="simulator-facility"
          hint={
            facilitiesQuery.isError
              ? 'Failed to load this client\'s facilities.'
              : facilitiesQuery.isSuccess && facilitiesQuery.data.length === 0
                ? 'This client has no facilities yet.'
                : undefined
          }
        >
          <select id="simulator-facility" className={INPUT_CLASS} value={facilityId} onChange={(event) => setFacilityId(event.target.value)}>
            <option value="">Choose a facility…</option>
            {(facilitiesQuery.data ?? []).map((facility) => (
              <option key={facility.facilityId} value={facility.facilityId}>
                {facility.name} ({facility.facilityId})
              </option>
            ))}
          </select>
        </Labeled>
        {facilityId !== '' && (
          <div className="text-xs text-ink-mid self-end pb-2">
            {workersQuery.isLoading
              ? 'Loading roster…'
              : workersQuery.isError
                ? 'Failed to load this facility\'s roster.'
                : `${workers.length.toLocaleString()} ${workers.length === 1 ? 'worker' : 'workers'} on the roster`}
          </div>
        )}
      </div>

      {facilityId !== '' && (
        <>
          {behaviorsQuery.isError ? (
            <LoadFailure size="xs" className="mb-4" message="Could not load what this facility measures, so only the standard calls are offered." onRetry={() => void behaviorsQuery.refetch()} retrying={behaviorsQuery.isFetching} />
          ) : (
            behaviors.length > 0 && <MeasuredBehaviors behaviors={behaviors} />
          )}
          <TabStrip activeKey={activeKind} className="mb-5">
            {tabs.map((tab) => (
              <button
                key={tab.kind}
                type="button"
                role="tab"
                aria-selected={activeKind === tab.kind}
                onClick={() => setKind(tab.kind)}
                className={segmentedTabClass(activeKind === tab.kind)}
              >
                {tab.label}
                {tab.kind.startsWith('custom:') && <span className="ml-1.5 text-[9px] font-bold uppercase tracking-wide opacity-70">Custom</span>}
                {tab.isOff && <span className={`ml-1.5 text-[9px] font-bold uppercase tracking-wide ${activeKind === tab.kind ? 'text-white' : 'text-coral'}`}>Off</span>}
              </button>
            ))}
          </TabStrip>
          {/* Keyed by facility (and kind) so a half-filled form does not carry over to a different one. */}
          {!hasScope ? (
            <p className="text-xs text-ink-mid">
              This client's token does not carry <span className="font-mono">{requiredScope}</span>, so the API would refuse this call. Grant the scope
              from the client's panel under Clients, then request a new token.
            </p>
          ) : customBehavior ? (
            <CustomBehaviorForm key={`${facilityId}:${customBehavior.key}`} behavior={customBehavior} session={session} facilityId={facilityId} workers={workers} />
          ) : activeKind === 'worker-onboard' ? (
            <WorkerOnboardForm
              key={facilityId}
              session={session}
              facilityId={facilityId}
              onOnboarded={() => {
                void queryClient.invalidateQueries({ queryKey: workersKey })
                void queryClient.invalidateQueries({ queryKey: ['admin', 'clients'] })
              }}
            />
          ) : activeKind === 'standing' ? (
            <StandingView key={facilityId} session={session} workers={workers} />
          ) : activeKind === 'performance' ? (
            <PerformanceSummaryForm key={facilityId} session={session} workers={workers} />
          ) : activeKind === 'bridge' ? (
            <BridgeEnrollmentForm key={facilityId} session={session} workers={workers} />
          ) : activeKind === 'bridge-callouts' ? (
            <BridgeCalloutsView key={facilityId} session={session} facilityId={facilityId} />
          ) : activeKind === 'preferences' ? (
            <ShiftPreferencesView key={facilityId} session={session} facilityId={facilityId} workers={workers} />
          ) : activeKind === 'schedule-run' ? (
            <ScheduleRunsView key={facilityId} session={session} facilityId={facilityId} />
          ) : activeKind === 'schedule-board' ? (
            <ScheduleBoardView key={facilityId} session={session} facilityId={facilityId} />
          ) : activeKind === 'census' ? (
            <CensusForm key={facilityId} session={session} facilityId={facilityId} />
          ) : activeKind === 'ranking' ? (
            <RankingForm key={facilityId} session={session} facilityId={facilityId} workers={workers} />
          ) : (
            <ShiftOutcomeForm
              key={`${facilityId}:${activeKind}`}
              kind={activeKind === 'shift-complete' ? 'completed' : activeKind === 'callout' ? 'callout' : 'no_show'}
              session={session}
              facilityId={facilityId}
              workers={workers}
            />
          )}
        </>
      )}
    </div>
  )
}

/**
 * Plays a partner's backend from the browser, so the calls a partner makes
 * (token issuance, shift outcomes, worker onboarding, pool ranking, Clinical Bridge days, performance summaries) have a view like every other API
 * call. It authenticates the way a real partner does - with the partner's own
 * key file - so it exercises the same checks. The admin API is used only to
 * fill the pickers (clients, facilities, rosters).
 */
export function PartnerSimulator({ api }: { api: AdminApi }) {
  const [clientId, setClientId] = useState('')
  const [session, setSession] = useState<PartnerSession | null>(null)

  const clientsQuery = useQuery({ queryKey: ['admin', 'clients'], queryFn: () => api.listClients() })
  const onboardedClients = (clientsQuery.data ?? []).filter((client) => client.clientId !== null)

  function changeClient(next: string): void {
    setClientId(next)
    // A token belongs to the client it was issued to.
    setSession(null)
  }

  return (
    <div className="max-w-3xl mx-auto px-7 py-6">
      <p className="text-sm text-ink-mid mb-5">
        Act as a partner's system: request a token with the partner's key, then make the calls its backend would make: report shift outcomes, onboard a worker, ask for a pool to be ranked, fetch a worker's standing, record a Clinical Bridge day a worker chose in its bid screen, or pull a performance summary for the HR system. Open{' '}
        <span className="font-bold text-ink">API calls</span> at the bottom right to see each request and response.
      </p>

      {clientsQuery.isLoading ? (
        <Skeleton lines={5} label="Loading clients" />
      ) : clientsQuery.isError ? (
        <LoadFailure message="Failed to load clients. Confirm the API is reachable." onRetry={() => void clientsQuery.refetch()} retrying={clientsQuery.isFetching} />
      ) : (
        <>
          <Section step="Step 1" title="Connect as a partner">
            <PartnerConnection clients={onboardedClients} clientId={clientId} session={session} onClientChange={changeClient} onSession={setSession} />
          </Section>
          <Section step="Step 2" title="Make a call">
            {session ? (
              <EventSender key={session.clientId} api={api} session={session} />
            ) : (
              <p className="text-xs text-ink-mid">Request a token first.</p>
            )}
          </Section>
        </>
      )}
    </div>
  )
}
